(() => {
  if (!('customElements' in window) || customElements.get('sticky-atc')) return;

  const MOBILE_QUERY = '(max-width: 768px)';
  const LOADING_CLASSES = ['btn--loading', 'is-loading', 'loading'];
  const FORM_EXCLUDE = [
    '.complementary-products',
    '[data-complementary-products]',
    'product-recommendations',
    '[data-product-recommendations]',
    '.product-recommendations',
    'sticky-atc',
  ].join(',');

  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.pastMainButton = false;
      this.currentVariantId = null;
      this.syncTimer = null;
      this.handleIntersect = this.handleIntersect.bind(this);
      this.handleMediaChange = this.updateVisibility.bind(this);
      this.handleVariantSignal = this.handleVariantSignal.bind(this);
      this.handleDocumentVariant = this.handleDocumentVariant.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleResize = this.updateOffset.bind(this);
      this.syncButton = this.syncButton.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

      this.section =
        document.getElementById(`shopify-section-${this.dataset.sectionId}`) ||
        this.closest('.shopify-section');
      if (!this.section) return;

      this.form = this.findForm();
      this.mainButton = this.form?.querySelector('[name="add"]') || this.form?.querySelector('[type="submit"]');
      this.button = this.querySelector('[data-sticky-atc-button]');
      this.labelEl = this.querySelector('[data-sticky-atc-label]');
      this.priceEl = this.querySelector('[data-sticky-atc-price]');

      if (!this.form || !this.mainButton || !this.button || !this.labelEl || !this.priceEl) {
        this.hidden = true;
        return;
      }

      this.compareEl = this.querySelector('[data-sticky-atc-compare]');
      this.variantEl = this.querySelector('[data-sticky-atc-variant]');
      this.imageEl = this.querySelector('[data-sticky-atc-image]');
      this.variants = this.parseVariants();
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);

      this.intersectionObserver = new IntersectionObserver(this.handleIntersect, { threshold: 0 });
      this.intersectionObserver.observe(this.mainButton);

      this.buttonObserver = new MutationObserver(this.syncButton);
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled', 'aria-busy'],
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.section.addEventListener('change', this.handleVariantSignal, true);
      this.section.addEventListener('variant:change', this.handleVariantSignal, true);
      document.addEventListener('variant:change', this.handleDocumentVariant);
      this.button.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mediaQuery.addListener(this.handleMediaChange);
      }

      this.initialized = true;
      this.syncButton();
      this.renderVariant(this.getSelectedVariantId());
      this.updateVisibility();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.intersectionObserver.disconnect();
      this.buttonObserver.disconnect();
      this.section.removeEventListener('change', this.handleVariantSignal, true);
      this.section.removeEventListener('variant:change', this.handleVariantSignal, true);
      document.removeEventListener('variant:change', this.handleDocumentVariant);
      this.button.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);

      if (typeof this.mediaQuery.removeEventListener === 'function') {
        this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mediaQuery.removeListener(this.handleMediaChange);
      }

      clearTimeout(this.syncTimer);
      document.body.classList.remove('sticky-atc-active');
      document.documentElement.style.removeProperty('--sticky-atc-height');
      this.initialized = false;
    }

    findForm() {
      const forms = [...this.section.querySelectorAll('form[action*="/cart/add"]')].filter(
        (form) =>
          !form.closest(FORM_EXCLUDE) &&
          form.querySelector('[name="id"]') &&
          form.querySelector('[name="add"], [type="submit"]')
      );
      return forms.find((form) => /^(AddToCartForm|product-form)/i.test(form.id)) || forms[0] || null;
    }

    parseVariants() {
      const node = this.querySelector('[data-sticky-atc-variants]');
      if (!node) return {};
      try {
        const data = JSON.parse(node.textContent);
        return data && typeof data === 'object' ? data : {};
      } catch (error) {
        console.warn('[sticky-atc] Invalid variant data', error);
        return {};
      }
    }

    getSelectedVariantId() {
      const input = this.form.querySelector('[name="id"]');
      const fromInput = input?.value ? String(input.value) : null;
      if (fromInput && this.variants[fromInput]) return fromInput;

      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl && this.variants[fromUrl]) return fromUrl;

      return fromInput;
    }

    handleVariantSignal(event) {
      const detailId = event?.detail?.variant?.id;
      if (detailId && this.variants[String(detailId)]) {
        this.renderVariant(String(detailId));
      }
      this.scheduleSync();
    }

    handleDocumentVariant(event) {
      const detailId = event?.detail?.variant?.id;
      if (!detailId || !this.variants[String(detailId)]) return;
      if (event.target instanceof Node && event.target !== document && !this.section.contains(event.target)) return;
      this.scheduleSync();
    }

    scheduleSync() {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.renderVariant(this.getSelectedVariantId());
      }, 0);
    }

    renderVariant(id) {
      if (!id || id === this.currentVariantId) return;
      const variant = this.variants[id];
      if (!variant) return;

      this.currentVariantId = id;
      this.priceEl.textContent = variant.price ?? '';

      if (this.compareEl) {
        this.compareEl.textContent = variant.compare || '';
        this.compareEl.hidden = !variant.compare;
      }

      if (this.variantEl) {
        this.variantEl.textContent = variant.title ?? '';
      }

      if (this.imageEl && variant.image && this.imageEl.getAttribute('src') !== variant.image) {
        this.imageEl.setAttribute('src', variant.image);
      }
    }

    syncButton() {
      const main = this.mainButton;
      const disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
      const loading =
        LOADING_CLASSES.some((className) => main.classList.contains(className)) ||
        main.getAttribute('aria-busy') === 'true';

      this.button.disabled = disabled;
      this.button.setAttribute('aria-busy', String(loading));

      const source = main.querySelector('[data-add-to-cart-text]') || main;
      const text = source.textContent.replace(/\s+/g, ' ').trim();
      if (text && this.labelEl.textContent.trim() !== text) {
        this.labelEl.textContent = text;
      }

      this.scheduleSync();
    }

    handleIntersect(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      this.pastMainButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.updateVisibility();
    }

    updateVisibility() {
      if (!this.initialized) return;
      const visible = this.mediaQuery.matches && this.pastMainButton;

      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.toggleAttribute('inert', !visible);
      document.body.classList.toggle('sticky-atc-active', visible);

      if (visible) this.updateOffset();
    }

    updateOffset() {
      if (!this.initialized) return;
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
    }

    handleClick(event) {
      event.preventDefault();
      if (this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
      if (this.mainButton.disabled) return;

      if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
        const invalidField = this.form.querySelector(':invalid');
        (invalidField || this.mainButton).scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.form.reportValidity();
        return;
      }

      this.mainButton.click();
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();